import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Outlet, useMatch, useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { Clock, Filter, Layers, PhoneMissed, Plus, SearchX, TriangleAlert } from 'lucide-react';
import { Header } from '@/components/Header';
import { EmptyState } from '@/components/EmptyState';
import { StageSheet } from '@/components/chat/StageSheet';
import { DealRow } from '@/components/pipeline/DealRow';
import { DealSpectrum } from '@/components/pipeline/DealSpectrum';
import { NewLeadSheet } from '@/components/pipeline/NewLeadSheet';
import { PipelinesSheet } from '@/components/pipeline/PipelinesSheet';
import { useProject } from '@/contexts/ProjectProvider';
import { crmKeys } from '@/hooks/useDealCard';
import { useCrmEvents } from '@/hooks/useCrmEvents';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { disqualifyDeal, getColleagues, moveDeal, type CardStage, type LostReason } from '@/api/crmCard';
import {
  getHomePipeline, getPipelines, listDeals, qualifyDeal, type DealListResponse, type DealRowData,
} from '@/api/pipeline';
import { DEAL_EVENT_TYPES } from '@/lib/crmEvents';
import { applyDealEvent } from '@/lib/dealListReducer';
import {
  countParams, dealListParams, dealRowModel, isLead, nextPageOf, pickPipeline, spectrumModel, type PipelineView,
} from '@/lib/pipelineQuery';
import { readPref, writePref } from '@/lib/storage';
import { useToast } from '@/store/toast';

const SAVED_KEY = (project: string) => `saleus-pipeline:${project}`;
const LIVE_TYPES = [...DEAL_EVENT_TYPES, 'call.changed'];
/** Пачку событий склеиваем в один перезапрос. */
const REFETCH_DEBOUNCE_MS = 800;

/** «Воронка»: проверяет проект, рисует список сделок; сделка без переписки открывается поверх (Outlet). */
export default function PipelinePage() {
  const { status, projectId, active } = useProject();
  const overlay = !!useMatch('/pipeline/deal/:dealId');

  let content: ReactNode;
  if (status !== 'ready') {
    content = <><Header title="Воронка" subtitle="Загружаем проект…" /><div className="scr"><DealSkeleton /></div></>;
  } else if (!projectId) {
    content = <><Header title="Воронка" /><div className="scr"><EmptyState icon={Layers} title="Проект не выбран" text="Выберите проект в разделе «Ещё»." /></div></>;
  } else {
    content = <PipelineContent key={projectId} projectId={projectId} projectName={active?.name ?? ''} />;
  }
  return (
    <>
      <div className="pipe-screen" inert={overlay} aria-hidden={overlay}>{content}</div>
      <Outlet />
    </>
  );
}

function DealSkeleton({ rows = 7 }: { rows?: number }) {
  return (
    <div role="status" aria-label="Загрузка сделок">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="dsk">
          <div className="flex gap-3"><div className="sk h-4 flex-1" /><div className="sk h-4 w-16" /></div>
          <div className="sk h-3.5 w-3/5" />
        </div>
      ))}
    </div>
  );
}

type SheetState = null | { kind: 'pipes' } | { kind: 'lead' } | { kind: 'reject'; deal: DealRowData };

function PipelineContent({ projectId, projectName }: { projectId: string; projectName: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast((t) => t.show);

  // ── Какая воронка ──
  const pipelinesQ = useQuery({ queryKey: ['crm', 'pipelines', projectId], queryFn: getPipelines, staleTime: 60_000 });
  const homeQ = useQuery({ queryKey: ['crm', 'home-pipeline', projectId], queryFn: getHomePipeline, staleTime: 10 * 60_000, retry: 0 });
  const peopleQ = useQuery({ queryKey: crmKeys.colleagues(projectId), queryFn: getColleagues, staleTime: 10 * 60_000, retry: 1 });
  const [saved, setSaved] = useState<string | null | undefined>(undefined);
  useEffect(() => { void readPref(SAVED_KEY(projectId)).then(setSaved); }, [projectId]);

  // CRM ещё отдаёт воронки организации с полем проекта: оставляем воронки активного проекта и общие.
  const list = useMemo(
    () => (pipelinesQ.data ?? []).filter((p) => !p.projectId || p.projectId === projectId),
    [pipelinesQ.data, projectId],
  );
  const ready = saved !== undefined && !homeQ.isPending;
  const current = ready ? pickPipeline(list, saved, homeQ.data?.pipelineId ?? null) : undefined;

  const meId = peopleQ.data?.find((c) => c.me)?.id ?? null;
  const names = useMemo(() => new Map((peopleQ.data ?? []).map((c) => [c.id, c.name])), [peopleQ.data]);
  const ownerName = useCallback((id: string) => (id === meId ? null : names.get(id) ?? null), [meId, names]);

  // ── Отбор ──
  const [mineWanted, setMine] = useState(true);
  const [missed, setMissed] = useState(false);
  const [overdue, setOverdue] = useState(false);
  const [stageId, setStageId] = useState<string | null>(null);
  const [scopeKind, setScopeKind] = useState<string | undefined>(undefined);
  const lead = isLead(scopeKind);
  const mine = mineWanted || !lead;

  const view: PipelineView | null = current
    ? { pipelineId: current.id, mine, meId, missed, overdue, stageId }
    : null;
  const listKey = ['crm', 'deals', projectId, view] as const;

  const dealsQ = useInfiniteQuery({
    queryKey: listKey,
    enabled: !!view && !peopleQ.isPending,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => listDeals(dealListParams(view!, pageParam)),
    getNextPageParam: (last: DealListResponse) => nextPageOf(last),
    staleTime: 20_000,
  });
  const pages = dealsQ.data?.pages;
  const first = pages?.[0];
  useEffect(() => { if (first?.scope?.kind) setScopeKind(first.scope.kind); }, [first?.scope?.kind]);
  const rows = useMemo(() => pages?.flatMap((p) => p.data) ?? [], [pages]);

  const countView = view ? { ...view, stageId: null, missed: false, overdue: false } : null;
  const missedQ = useQuery({
    queryKey: ['crm', 'deals-count', projectId, 'missed', countView],
    enabled: !!countView && !peopleQ.isPending,
    queryFn: async () => (await listDeals(countParams(countView!, 'missed'))).total,
    staleTime: 20_000,
  });
  const overdueQ = useQuery({
    queryKey: ['crm', 'deals-count', projectId, 'overdue', countView],
    enabled: !!countView && !peopleQ.isPending,
    queryFn: async () => (await listDeals(countParams(countView!, 'overdue'))).total,
    staleTime: 20_000,
  });

  const stages: CardStage[] = useMemo(() => current?.stages ?? [], [current]);
  const spectrum = useMemo(() => spectrumModel(stages, first?.stageTotals ?? [], stageId), [stages, first?.stageTotals, stageId]);

  // ── Живые события CRM ──
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refetchSoon = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      void qc.invalidateQueries({ queryKey: ['crm', 'deals', projectId] });
      void qc.invalidateQueries({ queryKey: ['crm', 'deals-count', projectId] });
    }, REFETCH_DEBOUNCE_MS);
  }, [qc, projectId]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  useCrmEvents(LIVE_TYPES, (ev) => {
    if (!view) return;
    const old = qc.getQueryData<InfiniteData<DealListResponse>>(listKey);
    const r = applyDealEvent(old, ev, { pipelineId: view.pipelineId });
    if (r.list && r.list !== old) qc.setQueryData(listKey, r.list as InfiniteData<DealListResponse>);
    if (r.refetch) refetchSoon();
  }, !!view);

  // ── Прокрутка, «потяни, чтобы обновить» ──
  const scrollRef = useRef<HTMLDivElement>(null);
  const refresh = useCallback(async () => {
    qc.setQueryData<InfiniteData<DealListResponse>>(listKey, (d) =>
      d ? { pages: d.pages.slice(0, 1), pageParams: d.pageParams.slice(0, 1) } : d);
    await Promise.all([
      dealsQ.refetch(),
      qc.invalidateQueries({ queryKey: ['crm', 'deals-count', projectId] }),
      qc.invalidateQueries({ queryKey: ['crm', 'pipelines', projectId] }),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc, dealsQ, projectId, JSON.stringify(listKey)]);
  const ptr = usePullToRefresh(scrollRef, refresh);
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el || !dealsQ.hasNextPage || dealsQ.isFetchingNextPage) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 600) void dealsQ.fetchNextPage();
  };
  // Новый отбор — к началу списка.
  const viewKey = JSON.stringify(view);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 0 }); }, [viewKey]);

  // ── Действия ──
  const [sheet, setSheet] = useState<SheetState>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const afterChange = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['crm', 'deals', projectId] });
    void qc.invalidateQueries({ queryKey: ['crm', 'deals-count', projectId] });
  }, [qc, projectId]);

  const open = useCallback((id: string) => {
    const d = rows.find((x) => x.id === id);
    if (d?.chatwootAccountId && d.chatwootDisplayId) {
      navigate(`/inbox/${d.chatwootAccountId}/${d.chatwootDisplayId}`, { state: { openDeal: true } });
    } else {
      navigate(`/pipeline/deal/${id}`);
    }
  }, [rows, navigate]);

  const accept = useCallback(async (id: string) => {
    setBusyId(id);
    try {
      await qualifyDeal(id);
      toast('Лид принят в работу');
      afterChange();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Не удалось принять', 5000);
    } finally {
      setBusyId(null);
    }
  }, [toast, afterChange]);

  const reject = useCallback((id: string) => {
    const d = rows.find((x) => x.id === id);
    if (d) setSheet({ kind: 'reject', deal: d });
  }, [rows]);

  const lose = async (deal: DealRowData, st: CardStage, reason: LostReason, comment: string) => {
    setSheet(null);
    setBusyId(deal.id);
    try {
      await disqualifyDeal(deal.id, { reasonId: reason.id, ...(comment ? { comment } : {}), stageId: st.id });
      toast('Лид отсеян');
      afterChange();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Не удалось отсеять', 5000);
    } finally {
      setBusyId(null);
    }
  };
  const move = async (deal: DealRowData, st: CardStage) => {
    setSheet(null);
    try {
      await moveDeal(deal.id, st.id);
      toast(`Стадия изменена: ${st.name}`);
      afterChange();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Не удалось сменить стадию', 5000);
    }
  };

  const pickPipelineId = (id: string) => {
    setSaved(id);
    void writePref(SAVED_KEY(projectId), id);
    setStageId(null);
    setSheet(null);
  };

  // ── Отрисовка ──
  if (pipelinesQ.isError) {
    return <><Header title="Воронка" /><div className="scr"><EmptyState icon={TriangleAlert} title="CRM не отвечает" text={pipelinesQ.error.message} action={{ label: 'Повторить', onClick: () => void pipelinesQ.refetch() }} /></div></>;
  }
  if (pipelinesQ.isPending || !ready) {
    return <><Header title="Воронка" subtitle={projectName} /><div className="scr"><DealSkeleton /></div></>;
  }
  if (!current) {
    return (
      <>
        <Header title="Воронка" subtitle={projectName} />
        <div className="scr">
          <EmptyState icon={Filter} title="Доступных воронок нет" text="Воронки открывает руководитель или администратор CRM: по отделу или лично вам." />
        </div>
      </>
    );
  }

  const models = rows.map((d) => ({ d, m: dealRowModel(d, stages, ownerName) }));
  const filtered = missed || overdue || !!stageId;
  const missedN = missedQ.data ?? 0;
  const overdueN = overdueQ.data ?? 0;

  return (
    <>
      <Header
        title={current.name}
        subtitle={mine ? 'Мои сделки' : 'Все сделки воронки'}
        onTitleClick={() => setSheet({ kind: 'pipes' })}
        titleExpanded={sheet?.kind === 'pipes'}
        actions={(
          <>
            <button type="button" className="ibtn" aria-pressed={missed} aria-label="Только с пропущенными звонками" onClick={() => { setMissed((v) => !v); setStageId(null); }}>
              <PhoneMissed aria-hidden />{missedN > 0 && <span className="cnt tnum">{missedN}</span>}
            </button>
            <button type="button" className="ibtn" aria-pressed={overdue} aria-label="Только с просроченными задачами" onClick={() => { setOverdue((v) => !v); setStageId(null); }}>
              <Clock aria-hidden />{overdueN > 0 && <span className="cnt tnum">{overdueN}</span>}
            </button>
          </>
        )}
      />
      <DealSpectrum model={spectrum} pipelineName={current.name} onSelect={setStageId} />
      <div className="flex-1 min-h-0 flex flex-col" style={{ borderTop: '1px solid var(--line)' }}>
        <div className="ptr" style={{ height: ptr.height }} aria-hidden={!ptr.busy}>
          {ptr.busy ? <span className="spinner" role="status" aria-label="Обновляем" /> : ptr.pulling ? 'Отпустите, чтобы обновить' : null}
        </div>
        <div className="scr" ref={scrollRef} onScroll={onScroll}>
          {dealsQ.isPending && <DealSkeleton />}
          {dealsQ.isError && !dealsQ.data && (
            <EmptyState icon={TriangleAlert} title="Сделки не загрузились" text={dealsQ.error.message} action={{ label: 'Повторить', onClick: () => void dealsQ.refetch() }} />
          )}
          {dealsQ.isSuccess && rows.length === 0 && (
            filtered
              ? <EmptyState icon={SearchX} title="Под эти условия сделок нет" text="Снимите отбор или выберите другую стадию." />
              : <EmptyState icon={Filter} title={mine ? 'Своих сделок пока нет' : 'В воронке пока нет сделок'} text={mine ? 'Новые лиды и сделки, назначенные на вас, появятся здесь.' : 'Заведите лид кнопкой «+».'} />
          )}
          {models.map(({ d, m }) => (
            <DealRow key={d.id} id={d.id} model={m} busy={busyId === d.id} onOpen={open} onAccept={(id) => void accept(id)} onReject={reject} />
          ))}
          {dealsQ.isFetchingNextPage && <div className="py-4 flex justify-center"><span className="spinner size-6" role="status" aria-label="Загружаем ещё" /></div>}
          {dealsQ.isError && !!dealsQ.data && (
            <div className="py-3 text-center text-sm text-muted">
              Не удалось обновить.{' '}
              <button type="button" className="text-accent font-semibold min-h-11 px-2" onClick={() => void dealsQ.refetch()}>Повторить</button>
            </div>
          )}
          <div style={{ height: 90 }} />
        </div>
      </div>
      <button type="button" className="fab" aria-label="Новый лид" onClick={() => setSheet({ kind: 'lead' })}><Plus aria-hidden /></button>

      {sheet?.kind === 'pipes' && (
        <PipelinesSheet
          pipelines={list}
          selectedId={current.id}
          lead={lead}
          mine={mine}
          onPick={pickPipelineId}
          onMine={(k) => { setMine(k); setStageId(null); setSheet(null); }}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet?.kind === 'lead' && (
        <NewLeadSheet
          project={projectId}
          pipelines={list}
          defaultPipelineId={current.id}
          onClose={() => setSheet(null)}
          onCreated={(id) => {
            setSheet(null);
            toast('Лид заведён');
            afterChange();
            navigate(`/pipeline/deal/${id}`);
          }}
        />
      )}
      {sheet?.kind === 'reject' && (
        <StageSheet
          project={projectId}
          pipelineName={current.name}
          stages={stages}
          currentId={sheet.deal.stageId}
          startAsking
          onPick={(st) => void move(sheet.deal, st)}
          onLost={(st, reason, comment) => void lose(sheet.deal, st, reason, comment)}
          onClose={() => setSheet(null)}
        />
      )}
    </>
  );
}
